import { salesDisplayUnitPrice } from './salesDisplayPrice';
import type { Aftercare, SalesLineType } from '@/types';
import { displayRemark, lineLabels, isDeferredMissed, isUnchargedResend } from '@/lib/salesAftercare.mjs';
/**
 * 单据分享工具模块
 * 支持将单据 HTML 生成为图片（PNG）或通过浏览器打印对话框保存为 PDF
 */

import html2canvas from 'html2canvas';
import { uploadShareImage } from './shareImage';
import type { ShareTemplate } from './shareTemplates';
import { SHARE_COLUMN_META, type ShareColumnKey } from './shareTemplates';
import type { PaymentWatermark } from './salesShare';

/** 模板列宽（px）；productName 返回 null = 自适应拉伸 */
function shareColumnWidth(key: ShareColumnKey): number | null {
  return SHARE_COLUMN_META[key]?.widthPx ?? null;
}

/**
 * #791 图片直发分享结果
 * - link: 主路线——图片已上传服务器，在线图片 URL 已复制剪贴板（对方点开即看）
 * - system: 辅路线——已调起系统分享面板（Web Share files），可直接把 PNG 发到微信/QQ
 * - longpress: 微信内置浏览器专用——全屏展示图片，用户长按图片可弹出微信「发送给朋友 / 保存到手机 / 识别二维码」菜单
 * - cancelled: 用户在系统分享面板点了取消
 * - download: 兜底——链接上传与系统分享均不可用，图片已下载到本地
 */
export type ImageDirectShareOutcome =
  | { mode: 'imageclip' }
  | { mode: 'link'; url: string; copied: boolean }
  | { mode: 'system' }
  | { mode: 'longpress' }
  | { mode: 'cancelled' }
  | { mode: 'download' };

/**
 * 是否在微信内置浏览器中
 * 微信内置浏览器不支持 navigator.share / navigator.canShare，因此直发图片走「长按图片」方案
 */
export function isWechatBrowser(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /MicroMessenger/i.test(navigator.userAgent);
}

/**
 * 全屏展示图片供长按发送（微信内置浏览器专用）。
 * 微信 H5 中长按图片会原生弹出「发送给朋友 / 保存图片 / 识别二维码」菜单，是微信内最稳的图片分享方式。
 * @returns 用户关闭浮层时 resolve（点关闭按钮 / 点击空白区 / 按 Esc）
 */
export function showImageForLongPress(imageUrl: string, hint = '长按图片 → 发送给朋友 / 保存到手机'): Promise<void> {
  return new Promise<void>((resolve) => {
    const wrap = document.createElement('div');
    wrap.setAttribute('data-changfa-image-share', '1');
    wrap.style.cssText = [
      'position:fixed', 'left:0', 'top:0', 'right:0', 'bottom:0',
      'background:rgba(0,0,0,0.94)', 'z-index:2147483647',
      'display:flex', 'flex-direction:column',
      'align-items:center', 'justify-content:center',
      'padding:24px 16px', 'box-sizing:border-box',
      // 防浮层文字被一起选中，干扰图片长按菜单
      '-webkit-user-select:none', 'user-select:none',
      '-webkit-touch-callout:none',
    ].join(';');

    const close = () => {
      if (wrap.parentNode) wrap.parentNode.removeChild(wrap);
      document.removeEventListener('keydown', onKey);
      resolve();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);

    const closeBtn = document.createElement('button');
    closeBtn.textContent = '×';
    closeBtn.setAttribute('aria-label', '关闭');
    closeBtn.style.cssText = [
      'position:absolute', 'top:14px', 'right:14px',
      'width:36px', 'height:36px', 'border-radius:50%',
      'background:rgba(255,255,255,0.18)', 'color:#fff',
      'border:0', 'font-size:22px', 'line-height:1',
      'cursor:pointer', 'display:flex',
      'align-items:center', 'justify-content:center',
    ].join(';');
    closeBtn.onclick = close;

    const hintEl = document.createElement('p');
    hintEl.textContent = hint;
    hintEl.style.cssText = [
      'color:#fff', 'font-size:15px', 'margin:0 0 14px',
      'text-align:center', 'line-height:1.5', 'font-weight:600',
    ].join(';');

    const img = document.createElement('img');
    img.src = imageUrl;
    img.alt = '单据图片，长按发送给朋友';
    img.style.cssText = [
      'max-width:100%', 'max-height:64vh',
      'object-fit:contain', 'background:#fff',
      'border-radius:8px', 'box-shadow:0 4px 20px rgba(0,0,0,0.4)',
      // 关键：允许长按弹出微信原生菜单；user-select:none 防文字选择干扰
      '-webkit-touch-callout:default',
      '-webkit-user-select:none', 'user-select:none',
      '-webkit-user-drag:none',
    ].join(';');

    const subHint = document.createElement('p');
    subHint.textContent = '按住图片稍作停留即可弹出菜单';
    subHint.style.cssText = [
      'color:rgba(255,255,255,0.6)', 'font-size:12px',
      'margin:14px 0 0', 'text-align:center',
    ].join(';');

    // 兜底：保存图片按钮（长按菜单调不出时直接保存到相册/下载）
    const saveBtn = document.createElement('button');
    saveBtn.textContent = '保存图片到本机';
    saveBtn.style.cssText = [
      'margin-top:16px', 'border:0', 'border-radius:10px',
      'padding:11px 30px', 'font-size:14px', 'font-weight:600',
      'background:#0f766e', 'color:#fff', 'cursor:pointer',
    ].join(';');
    saveBtn.onclick = () => {
      try {
        downloadDataUrl(imageUrl, `changfa-${Date.now()}.png`);
      } catch (err) {
        console.warn('保存图片失败', err);
      }
    };

    wrap.appendChild(closeBtn);
    wrap.appendChild(hintEl);
    wrap.appendChild(img);
    wrap.appendChild(subHint);
    wrap.appendChild(saveBtn);
    wrap.onclick = (e) => { if (e.target === wrap) close(); };

    document.body.appendChild(wrap);
  });
}

/**
 * 复制文本到剪贴板（自动复制失败时降级 execCommand，再失败返回 false 由调用方提示手动复制）
 */
export async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

/**
 * 当前环境是否支持 Web Share API（navigator.share / navigator.canShare）
 * 注意：微信内置浏览器不支持 files 分享，会走到主路线（链接）或兜底（下载）
 */
export function canShareFiles(): boolean {
  return typeof navigator !== 'undefined'
    && typeof navigator.share === 'function'
    && typeof navigator.canShare === 'function';
}

/**
 * PNG data URL → File 对象（供 Web Share files 直发）
 */
export async function dataUrlToPngFile(dataUrl: string, fileName: string): Promise<File> {
  const blob = await (await fetch(dataUrl)).blob();
  return new File([blob], fileName, { type: 'image/png' });
}

/**
 * 尝试调起系统分享面板直发 PNG 图片（#791 辅路线）
 * @returns 'shared' 已调起并完成分享；'cancelled' 用户取消；'unsupported' 当前环境不支持
 */
export async function tryShareImageFile(
  dataUrl: string,
  fileName: string,
  title?: string
): Promise<'shared' | 'cancelled' | 'unsupported'> {
  if (!canShareFiles()) return 'unsupported';
  try {
    const file = await dataUrlToPngFile(dataUrl, fileName);
    if (!navigator.canShare({ files: [file] })) return 'unsupported';
    await navigator.share({ files: [file], title: title || fileName });
    return 'shared';
  } catch (err) {
    // 用户在分享面板点了取消（AbortError），不算失败
    if (err instanceof DOMException && err.name === 'AbortError') return 'cancelled';
    return 'unsupported';
  }
}

/**
 * data URL 直接下载（兜底）
 */
export function downloadDataUrl(dataUrl: string, filename: string): void {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/**
 * #792 data URL → Blob
 */
export async function dataUrlToBlob(dataUrl: string): Promise<Blob> {
  return await (await fetch(dataUrl)).blob();
}

/**
 * #792 把 PNG 图片本体写入系统剪贴板（区别于 writeText 只写文本）。
 * 成功后用户可直接 Ctrl+V 粘贴到微信桌面版 / QQ / WhatsApp / 邮件，直接出图。
 * Chrome 76+ / Edge 79+ / Safari 13.1+ / Firefox 127+ 支持；需 HTTPS 且页面有焦点。
 */
export async function copyImageToClipboard(blob: Blob, companionText?: string): Promise<boolean> {
  if (typeof ClipboardItem === 'undefined') return false;
  const nav = navigator as Navigator & { clipboard?: Clipboard };
  if (!nav.clipboard || typeof nav.clipboard.write !== 'function') return false;
  // #795 Blob 包成 File 提升微信桌面版对剪贴板图片的识别率（部分客户端对裸 Blob 数据的 CF_DIB 转换不稳）
  const imageFile = blob instanceof File ? blob : new File([blob], 'share.png', { type: 'image/png' });
  const writeItems = async (extra?: Record<string, Blob>) => {
    await nav.clipboard!.write([new ClipboardItem({ 'image/png': imageFile, ...(extra || {}) })]);
  };
  try {
    if (companionText) {
      // 双格式：图片 + 纯文本链接同时写入——微信无法粘贴图片时，Ctrl+V 仍可粘出链接兜底
      try {
        await writeItems({ 'text/plain': new Blob([companionText], { type: 'text/plain' }) });
        return true;
      } catch {
        // 双格式不受支持（部分浏览器只允许图片），退回仅写图片
      }
    }
    await writeItems();
    return true;
  } catch (err) {
    console.warn('复制图片到剪贴板失败:', err);
    return false;
  }
}

/**
 * #792 显式弹出分享链接对话框（大字号展示 URL + 复制按钮 + 打开链接按钮）。
 * 即使 copyTextToClipboard 因焦点/权限问题失败，用户也能手动选中复制。
 */
export function showShareUrlDialog(url: string): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    const wrap = document.createElement('div');
    wrap.setAttribute('data-changfa-share-url-dialog', '1');
    wrap.style.cssText = [
      'position:fixed', 'left:0', 'top:0', 'right:0', 'bottom:0',
      'background:rgba(0,0,0,0.55)', 'z-index:2147483647',
      'display:flex', 'align-items:center', 'justify-content:center',
      'padding:20px', 'box-sizing:border-box',
    ].join(';');

    let copiedResult = false;
    const close = () => {
      if (wrap.parentNode) wrap.parentNode.removeChild(wrap);
      document.removeEventListener('keydown', onKey);
      resolve(copiedResult);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);

    const card = document.createElement('div');
    card.style.cssText = [
      'background:#fff', 'border-radius:16px', 'padding:24px 22px 18px',
      'width:100%', 'max-width:420px', 'box-sizing:border-box',
      'box-shadow:0 12px 40px rgba(0,0,0,0.25)',
      'font-family:-apple-system,"PingFang SC","Microsoft YaHei",sans-serif',
    ].join(';');

    const title = document.createElement('div');
    title.textContent = '图片链接已生成';
    title.style.cssText = 'font-size:16px;font-weight:700;color:#0f766e;margin-bottom:6px';

    const sub = document.createElement('div');
    sub.textContent = '复制下面的链接，粘贴到微信即可发送（对方点开直接看图）';
    sub.style.cssText = 'font-size:12px;color:#6b7280;margin-bottom:12px;line-height:1.5';

    const urlBox = document.createElement('div');
    urlBox.textContent = url;
    urlBox.style.cssText = [
      'font-size:13px', 'color:#1f2937', 'background:#f3f4f6',
      'border-radius:8px', 'padding:10px 12px',
      'word-break:break-all', 'line-height:1.5',
      'user-select:all', '-webkit-user-select:all',
      'margin-bottom:14px',
    ].join(';');

    const btnRow = document.createElement('div');
    btnRow.style.cssText = 'display:flex;gap:10px';

    const makeBtn = (text: string, primary: boolean, onClick: () => void) => {
      const b = document.createElement('button');
      b.textContent = text;
      b.style.cssText = [
        'flex:1', 'border:0', 'border-radius:10px', 'padding:10px 0',
        'font-size:14px', 'font-weight:600', 'cursor:pointer',
        primary ? 'background:#0f766e;color:#fff' : 'background:#e8f5f1;color:#0f766e',
      ].join(';');
      b.onclick = onClick;
      return b;
    };

    const copyBtn = makeBtn('复制链接', true, async () => {
      const ok = await copyTextToClipboard(url);
      copiedResult = ok;
      copyBtn.textContent = ok ? '已复制 ✓' : '复制失败，请长按选中';
    });

    const openBtn = makeBtn('打开链接', false, () => {
      window.open(url, '_blank', 'noopener');
    });

    btnRow.appendChild(copyBtn);
    btnRow.appendChild(openBtn);

    const closeRow = document.createElement('div');
    closeRow.style.cssText = 'text-align:center;margin-top:10px';
    const closeLink = document.createElement('a');
    closeLink.textContent = '关闭';
    closeLink.href = 'javascript:void(0)';
    closeLink.style.cssText = 'font-size:12px;color:#9ca3af;text-decoration:none';
    closeLink.onclick = close;
    closeRow.appendChild(closeLink);

    card.appendChild(title);
    card.appendChild(sub);
    card.appendChild(urlBox);
    card.appendChild(btnRow);
    card.appendChild(closeRow);
    wrap.appendChild(card);
    wrap.onclick = (e) => { if (e.target === wrap) close(); };
    document.body.appendChild(wrap);
  });
}

/**
 * #795 图片复制成功后的结果卡片：缩略图 + 明确的操作出口。
 * 背景：Windows 微信桌面版对 Chrome 写入的剪贴板图片（CF_DIB）支持不稳，纯 toast
 * 「已复制」无法兜底。此卡片提供三条出路：
 * 1. 重新复制图片——点击本身是全新的用户手势（transient activation 重新计时），
 *    可绕过「图片生成耗时超过激活窗口导致 clipboard.write 被拒」的问题；
 *    同时先上传拿真实链接，双格式（图片+链接）一起写入剪贴板；
 * 2. 保存图片——下载 PNG 后可直接拖进微信聊天框发送；
 * 3. 复制图片链接——上传后复制在线链接，对方点开即看图（30 天有效）。
 */
export function showImageShareResultDialog(dataUrl: string, name: string): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    const wrap = document.createElement('div');
    wrap.setAttribute('data-changfa-share-image-result-dialog', '1');
    wrap.style.cssText = [
      'position:fixed', 'left:0', 'top:0', 'right:0', 'bottom:0',
      'background:rgba(0,0,0,0.55)', 'z-index:2147483647',
      'display:flex', 'align-items:center', 'justify-content:center',
      'padding:20px', 'box-sizing:border-box',
    ].join(';');

    const close = () => {
      if (wrap.parentNode) wrap.parentNode.removeChild(wrap);
      document.removeEventListener('keydown', onKey);
      resolve(true);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);

    const card = document.createElement('div');
    card.style.cssText = [
      'background:#fff', 'border-radius:16px', 'padding:20px 18px 14px',
      'width:100%', 'max-width:420px', 'box-sizing:border-box',
      'box-shadow:0 12px 40px rgba(0,0,0,0.25)',
      'font-family:-apple-system,"PingFang SC","Microsoft YaHei",sans-serif',
      'max-height:90vh', 'overflow-y:auto',
    ].join(';');

    const title = document.createElement('div');
    title.textContent = '图片已复制到剪贴板';
    title.style.cssText = 'font-size:16px;font-weight:700;color:#0f766e;margin-bottom:6px';

    const sub = document.createElement('div');
    sub.textContent = '去微信 / QQ 聊天框按 Ctrl+V 粘贴即可发送。若无法粘贴，请点「保存图片」后把图片拖进聊天框。';
    sub.style.cssText = 'font-size:12px;color:#6b7280;margin-bottom:12px;line-height:1.6';

    const img = document.createElement('img');
    img.src = dataUrl;
    img.alt = name;
    img.style.cssText = [
      'display:block', 'max-width:100%', 'max-height:220px',
      'margin:0 auto 14px', 'border-radius:8px',
      'border:1px solid #e5e7eb', 'object-fit:contain',
    ].join(';');

    const btnRow = document.createElement('div');
    btnRow.style.cssText = 'display:flex;gap:10px;margin-bottom:10px';

    const makeBtn = (text: string, primary: boolean, onClick: () => void) => {
      const b = document.createElement('button');
      b.textContent = text;
      b.style.cssText = [
        'flex:1', 'border:0', 'border-radius:10px', 'padding:10px 0',
        'font-size:14px', 'font-weight:600', 'cursor:pointer',
        primary ? 'background:#0f766e;color:#fff' : 'background:#e8f5f1;color:#0f766e',
      ].join(';');
      b.onclick = onClick;
      return b;
    };

    // 重新复制：全新用户手势 + 双格式（图片本体 + 在线链接）写入
    const recopyBtn = makeBtn('重新复制图片', true, async () => {
      if (recopyBtn.disabled) return;
      recopyBtn.disabled = true;
      recopyBtn.textContent = '复制中…';
      try {
        const blob = await dataUrlToBlob(dataUrl);
        let link: string | undefined;
        try {
          ({ url: link } = await uploadShareImage(dataUrl, name));
        } catch {
          // 上传失败不影响图片复制，仅少一份文本兜底
        }
        const ok = await copyImageToClipboard(blob, link);
        recopyBtn.textContent = ok ? '已重新复制 ✓' : '失败，请用保存图片';
      } catch {
        recopyBtn.textContent = '失败，请用保存图片';
      }
      recopyBtn.disabled = false;
    });

    const saveBtn = makeBtn('保存图片', false, () => {
      downloadDataUrl(dataUrl, `${name}.png`);
      saveBtn.textContent = '已保存 ✓';
    });

    btnRow.appendChild(recopyBtn);
    btnRow.appendChild(saveBtn);

    // 复制在线链接（30 天有效，对方点开直接看图）
    const linkRow = document.createElement('div');
    linkRow.style.cssText = 'text-align:center';
    const linkBtn = document.createElement('button');
    linkBtn.textContent = '复制图片链接（对方点开即看图，30天有效）';
    linkBtn.style.cssText = [
      'border:0', 'background:transparent', 'color:#0f766e',
      'font-size:12.5px', 'cursor:pointer', 'padding:6px 10px',
      'text-decoration:underline',
    ].join(';');
    linkBtn.onclick = async () => {
      if (linkBtn.disabled) return;
      linkBtn.disabled = true;
      linkBtn.textContent = '上传中…';
      try {
        const { url } = await uploadShareImage(dataUrl, name);
        const ok = await copyTextToClipboard(url);
        linkBtn.textContent = ok ? '链接已复制 ✓' : '复制失败，请重试';
        if (!ok) linkBtn.disabled = false;
      } catch {
        linkBtn.textContent = '上传失败，请重试';
        linkBtn.disabled = false;
      }
    };
    linkRow.appendChild(linkBtn);

    const closeRow = document.createElement('div');
    closeRow.style.cssText = 'text-align:center;margin-top:8px';
    const closeLink = document.createElement('a');
    closeLink.textContent = '关闭';
    closeLink.href = 'javascript:void(0)';
    closeLink.style.cssText = 'font-size:12px;color:#9ca3af;text-decoration:none';
    closeLink.onclick = close;
    closeRow.appendChild(closeLink);

    card.appendChild(title);
    card.appendChild(sub);
    card.appendChild(img);
    card.appendChild(btnRow);
    card.appendChild(linkRow);
    card.appendChild(closeRow);
    wrap.appendChild(card);
    wrap.onclick = (e) => { if (e.target === wrap) close(); };
    document.body.appendChild(wrap);
  });
}

/**
 * #792 图片直发分享（免下载）策略链：
 * 1. 微信内置浏览器——Web Share / ClipboardItem 均不可用，先上传得到真实 http URL
 *    （比 data URL 更稳，微信长按菜单对真实 URL 图片支持可靠），再全屏展示供长按发送；
 * 2. 图片剪贴板——navigator.clipboard.write 写入 PNG 本体，Ctrl+V 粘贴到微信桌面版/QQ/邮件直接出图；
 * 3. 系统分享面板——Web Share files；
 * 4. 链接兜底——上传后弹出显式链接对话框（复制/打开），即使自动复制失败用户也能手动拿到链接；
 * 5. 最终兜底——下载 PNG。
 * @param html 完整的 HTML 文档字符串
 * @param name 业务标识（单号），用于文件名与上传前缀
 * @param preferSystemShare 非微信环境下是否允许系统分享面板（默认 true）
 */
export async function shareImageDirect(html: string, name: string, preferSystemShare = true): Promise<ImageDirectShareOutcome> {
  const dataUrl = await generateShareImageDataUrl(html);

  // 微信内置浏览器：长按图片方案（上传真实 URL 后再展示，data URL 长按菜单不稳定）
  if (isWechatBrowser()) {
    let imageUrl = dataUrl;
    try {
      const { url } = await uploadShareImage(dataUrl, name);
      imageUrl = url;
    } catch (err) {
      console.warn('微信内图片上传失败，降级用 data URL 展示', err);
    }
    await showImageForLongPress(imageUrl, `${name} · 长按图片 → 发送给朋友 / 保存到手机`);
    return { mode: 'longpress' };
  }

  // 主路线：图片本体写入剪贴板（必须最先做——后续任何弹窗/分享面板都会导致页面失焦而失败）
  try {
    const blob = await dataUrlToBlob(dataUrl);
    if (blob.size > 0 && await copyImageToClipboard(blob)) {
      // #795 复制成功后弹结果卡片：微信无法粘贴时还有 保存图片/复制链接 两条出路，
      // 「重新复制图片」按钮的点击是全新用户手势，可绕过 transient activation 过期问题
      await showImageShareResultDialog(dataUrl, name);
      return { mode: 'imageclip' };
    }
  } catch (err) {
    console.warn('图片剪贴板路线失败，降级系统分享', err);
  }

  // 辅路线：系统分享面板直发图片
  if (preferSystemShare && canShareFiles()) {
    const shared = await tryShareImageFile(dataUrl, `${name}.png`, name);
    if (shared === 'shared') return { mode: 'system' };
    if (shared === 'cancelled') return { mode: 'cancelled' };
  }

  // 链接兜底：上传 + 显式链接对话框（手动复制永远可行）
  try {
    const { url } = await uploadShareImage(dataUrl, name);
    const copied = await showShareUrlDialog(url);
    return { mode: 'link', url, copied };
  } catch (err) {
    console.warn('图片链接上传失败，降级本地下载', err);
  }

  // 最终兜底：下载 PNG
  downloadDataUrl(dataUrl, `${name}.png`);
  return { mode: 'download' };
}

/**
 * 将 HTML 字符串渲染到隐藏的 iframe 中，并返回 iframe 的 body 元素
 * 用于 html2canvas 截图
 */
async function renderHtmlToIframe(html: string): Promise<{ iframe: HTMLIFrameElement; body: HTMLElement }> {
  return new Promise((resolve, reject) => {
    const iframe = document.createElement('iframe');
    // sandbox 白名单不含 allow-scripts：即使 sanitizeHtml 漏拦内联脚本，iframe 内也无法执行 JS
    // 保留 allow-same-origin 使 html2canvas 仍可读取 contentDocument 完成截图
    iframe.setAttribute('sandbox', 'allow-same-origin');
    iframe.style.cssText = 'position:fixed;left:-9999px;top:0;width:800px;height:1200px;border:0;background:#fff;';
    document.body.appendChild(iframe);

    const cleanup = () => {
      setTimeout(() => {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      }, 1000);
    };

    iframe.onload = () => {
      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!doc || !doc.body) {
          reject(new Error('无法获取 iframe 文档'));
          cleanup();
          return;
        }
        // 等待渲染 + 字体加载
        setTimeout(() => {
          resolve({ iframe, body: doc.body });
        }, 300);
      } catch (err) {
        reject(err);
        cleanup();
      }
    };

    iframe.onerror = () => {
      reject(new Error('iframe 渲染失败'));
      cleanup();
    };

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) {
      reject(new Error('无法写入 iframe'));
      return;
    }
    doc.open();
    doc.write(html);
    doc.close();
  });
}

/**
 * 移除 HTML 中的脚本与事件处理器，防止同源 XSS
 */
export function sanitizeHtml(html: string): string {
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<script\b[^>]*>/gi, '')
    .replace(/\s*on\w+\s*=\s*(["'][^"']*["']|[^\s>]+)/gi, '');
}

function toFiniteNumber(v: unknown, fallback = 0): number {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function toSafeFontSize(v: unknown): number {
  const n = toFiniteNumber(v, 13);
  return Math.max(8, Math.min(32, n));
}

/**
 * 分享为图片（PNG）
 * @param html 完整的 HTML 文档字符串
 * @param filename 下载文件名（不含扩展名）
 */
export async function shareAsImage(html: string, filename: string): Promise<void> {
  const cleanHtml = sanitizeHtml(html);
  const { iframe, body } = await renderHtmlToIframe(cleanHtml);

  try {
    const canvas = await html2canvas(body, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      windowWidth: 800,
      logging: false,
    });

    canvas.toBlob((blob) => {
      if (!blob) {
        throw new Error('图片生成失败');
      }
      downloadBlob(blob, `${filename}.png`);
    }, 'image/png');
  } finally {
    setTimeout(() => {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    }, 500);
  }
}

/**
 * 生成 HTML 对应的 PNG 图片 Data URL（不下载，用于上传图床/服务器）
 * @param html 完整的 HTML 文档字符串
 * @returns base64 data URL (image/png)
 */
export async function generateShareImageDataUrl(html: string): Promise<string> {
  const cleanHtml = sanitizeHtml(html);
  const { iframe, body } = await renderHtmlToIframe(cleanHtml);
  try {
    const canvas = await html2canvas(body, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      windowWidth: 800,
      logging: false,
    });
    return canvas.toDataURL('image/png');
  } finally {
    setTimeout(() => {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    }, 500);
  }
}

/**
 * 分享为 PDF（通过浏览器打印对话框，用户选择"另存为 PDF"）
 * @param html 完整的 HTML 文档字符串
 * @param filename 文件名（用于窗口标题提示）
 */
export async function shareAsPDF(html: string, filename: string): Promise<void> {
  // 移除自动打印脚本与事件处理器，改为手动触发
  const cleanHtml = sanitizeHtml(html);
  const printWindow = window.open('', '_blank', 'width=800,height=600');
  if (!printWindow) {
    throw new Error('无法打开打印窗口，请检查浏览器弹窗设置');
  }
  printWindow.document.write(cleanHtml);
  printWindow.document.close();
  printWindow.document.title = filename;
  printWindow.focus();
  // 延迟触发打印对话框，用户可选择"另存为 PDF"
  setTimeout(() => {
    printWindow.print();
  }, 300);
}

/**
 * 下载 Blob 文件
 */
function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * 生成报价单/送货单/采购单分享 HTML
 * 复用 QuotationPage 的打印样式，但去除 script 和自动打印
 * #798 方案 C：传入 template（分享模板）时按模板驱动列头/列顺序/列名/字号/开关；
 * 未传 template 时回退内置 7 列布局（保证历史链接分享 ShareViewer 向后兼容）。
 */
export function generateQuotationShareHtml(params: {
  quotationId: string;
  customerName: string;
  customerPhone?: string;
  customerAddress?: string;
  date: string;
  items: Array<{
    productName: string;
    qty: number;
    subUnitQty: number;
    perUnitQty?: number;
    subUnit: string;
    wholesalePrice: number;
    amount: number;
    packSize?: number;
    paid?: boolean;
    printPrice?: boolean;
    showPrice?: boolean;
    showAmount?: boolean;
    remark?: string;
    lineType?: SalesLineType;
    aftercare?: Aftercare;
    priceLabel?: '优惠价' | '优惠退货';
  }>;
  totalAmount: number;
  paidAmount: number;
  printHidePaid: boolean;
  fontSize: number;
  remark?: string;
  docTitle?: string;
  docType?: 'quotation' | 'delivery' | 'sales' | 'inbound' | 'po';
  paymentWatermark?: PaymentWatermark;
  template?: ShareTemplate;
}): string {
  const { quotationId, customerName, customerPhone, customerAddress, date, items, totalAmount, paidAmount, printHidePaid, fontSize: rawFontSize, remark, docTitle, docType, paymentWatermark, template } = params;
  // #533 调用方传入的 totalAmount 已按规则扣除：已付款 / 隐藏价格 / 换货（不计入）/ 退货（负扣减）
  const displayTotal = toFiniteNumber(totalAmount, 0);
  const displayPaidAmount = toFiniteNumber(paidAmount, 0);
  const safeFontSize = template ? Math.max(8, Math.min(18, toFiniteNumber(template.fontSize, rawFontSize))) : toSafeFontSize(rawFontSize);
  const title = docTitle || (docType === 'po' ? '昌发香行采购单' : '昌发香行送货单');
  const partyLabel = docType === 'inbound' || docType === 'po' ? '供应商' : '收货人';
  const salesQuantity = docType !== 'inbound' && docType !== 'po';

  // 对 items 做类型强制与字段净化，防止 URL/AI 等不可信来源注入 HTML/事件处理器
  const safeItems = items
    .map((it) => {
      const lineType = ['normal', 'return', 'exchange', 'outOfStock', 'missed', 'resend', 'cancelled'].includes(String(it.lineType)) ? (String(it.lineType) as typeof it.lineType) : 'normal';
      return {
        productName: String(it.productName || ''),
        qty: toFiniteNumber(it.qty, 0),
        subUnitQty: toFiniteNumber(it.subUnitQty, 0),
        perUnitQty: it.perUnitQty == null ? undefined : toFiniteNumber(it.perUnitQty, 0),
        subUnit: String(it.subUnit || ''),
        wholesalePrice: toFiniteNumber(it.wholesalePrice, 0),
        priceLabel: ['优惠价', '优惠退货'].includes(it.priceLabel || '') ? it.priceLabel : '',
        amount: toFiniteNumber(it.amount, 0),
        packSize: it.packSize == null ? undefined : toFiniteNumber(it.packSize, 0),
        paid: !!it.paid,
        printPrice: it.printPrice !== false,
        showPrice: it.showPrice !== false,
        unchargedResend: isUnchargedResend(it),
        showAmount: lineType !== 'cancelled' && !isDeferredMissed(it) && it.showAmount !== false,
        remark: displayRemark(it),
        lineType,
      };
    })
    // 过滤编辑页默认填充的空行占位，避免分享图片出现「产品名空、数量金额都为 0」的空白行
    .filter((it) => !(it.productName === '' && it.qty <= 0 && it.subUnitQty <= 0 && it.amount <= 0));

  // #723 微信账单卡片风格：行类型/已付款用柔和色徽章，备注小字，退货整行浅红提示
  const badge = (text: string, bg: string, color: string) =>
    `<span style="display:inline-block;font-size:9px;line-height:1;padding:2px 5px;border-radius:4px;background:${bg};color:${color};font-weight:600;margin-right:4px;vertical-align:middle">${text}</span>`;

  const headerFontPx = Math.max(9, Math.round(safeFontSize * 0.85));

  // #798 模板驱动列：只渲染 visible 列，顺序即模板顺序；未传模板回退内置 7 列
  const fallbackKeys = ['index', 'productName', 'qty', 'subUnitQty', 'subUnit', 'amount', 'remark'] as const;
  const fallbackLabels: Record<(typeof fallbackKeys)[number], string> = {
    index: '序号', productName: '产品名称', qty: '件数', subUnitQty: '数量', subUnit: '单位', amount: '金额', remark: '备注',
  };
  const activeColumns: Array<{ key: ShareColumnKey; label: string }> = template
    ? template.columns.filter((c) => c.visible).map((c) => ({ key: c.key, label: c.label }))
    : fallbackKeys.map((key) => ({ key: key as ShareColumnKey, label: fallbackLabels[key] }));

  const cell = (it: (typeof safeItems)[number], idx: number, key: ShareColumnKey): {
    text?: string; html?: string; align: string; color?: string; weight?: string; pad?: string;
  } => {
    // #537 分享显示只由「隐藏单价/隐藏金额」选择决定（换货/退货/已付款/不计价不强制隐藏）
    // #538 缺货行单价/金额显示 —
    const hidePrice = it.showPrice === false || it.lineType === 'outOfStock';
    const hideAmount = it.showAmount === false || it.lineType === 'outOfStock';
    switch (key) {
      case 'index': return { text: String(idx + 1), align: 'center', color: '#9ca3af' };
      case 'productName': {
        let sub = '';
        if (it.lineType === 'return') sub += badge('退货', '#fee2e2', '#dc2626');
        else if (it.lineType === 'exchange') sub += badge('换货', '#fef3c7', '#b45309');
        else if (it.lineType === 'outOfStock') sub += badge('缺货', '#e0e7ff', '#4338ca');
        else if (['missed', 'resend', 'cancelled'].includes(it.lineType || '')) sub += badge(lineLabels[it.lineType!], '#fef3c7', '#92400e');
        if (it.paid) sub += badge('已付款', '#d1fae5', '#059669');
        const name = escapeHtml(it.productName);
        return {
          html: `<div style="font-size:${safeFontSize}px;color:#1f2937">${name}</div>${sub ? `<div style="margin-top:2px;line-height:1.4">${sub}</div>` : ''}`,
          align: 'left', pad: '7px 8px',
        };
      }
      case 'packSize': return { text: it.packSize != null && it.packSize > 0 ? String(it.packSize) : '', align: 'center', color: '#374151' };
      case 'qty': return { text: it.qty > 0 ? String(it.qty) : '', align: 'center', color: '#374151' };
      case 'subUnitQty': return { text: String(salesQuantity ? (it.perUnitQty ?? (it.qty > 0 ? it.subUnitQty / it.qty : it.subUnitQty)) : it.subUnitQty), align: 'center', color: '#374151' };
      case 'subUnit': return { text: escapeHtml(it.subUnit), align: 'center', color: '#374151' };
      case 'price': return { text: it.unchargedResend ? '' : hidePrice ? '—' : '¥' + salesDisplayUnitPrice(it).toFixed(2), align: 'right', color: '#1f2937', weight: '500' };
      case 'amount': return { text: it.unchargedResend ? '' : hideAmount ? '—' : '¥' + it.amount.toFixed(2), align: 'right', color: '#1f2937', weight: '600' };
      case 'remark': return { text: it.remark, align: 'left', color: '#6b7280', pad: '7px 6px' };
    }
  };

  const rows = safeItems.map((it, idx) => {
    const rowBg = it.lineType === 'return' ? 'background:#fef2f2;' : '';
    const tds = activeColumns.map((col) => {
      const c = cell(it, idx, col.key);
      const align = c.align;
      const html = 'html' in c ? c.html : escapeHtml(String(c.text));
      const pad = 'pad' in c ? c.pad : '7px 4px';
      const weight = 'weight' in c && c.weight ? `;font-weight:${c.weight}` : '';
      const color = 'color' in c && c.color ? `;color:${c.color}` : '';
      return `<td style="padding:${pad};border-bottom:1px solid #eef1f4;text-align:${align};font-size:${safeFontSize}px${color}${weight}">${html}</td>`;
    }).join('');
    return `<tr style="${rowBg}">${tds}</tr>`;
  }).join('');

  const thead = activeColumns.map((col) => {
    const widthPx = shareColumnWidth(col.key);
    return `<th style="${widthPx ? `width:${widthPx}px;` : ''}padding:6px 6px;font-size:${headerFontPx}px;color:#0f766e;font-weight:600;text-align:${col.key === 'productName' || col.key === 'remark' ? 'left' : col.key === 'amount' || col.key === 'price' ? 'right' : 'center'}">${escapeHtml(salesQuantity && col.key === 'subUnitQty' ? '数量' : col.label)}</th>`;
  }).join('');

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${escapeHtml(title)} - ${escapeHtml(quotationId)}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: -apple-system, 'PingFang SC', 'Microsoft YaHei', 'SimSun', sans-serif; color: #222; background: #f2f4f7; padding: 24px; width: 800px; }
  .card { position: relative; background: #fff; border-radius: 14px; box-shadow: 0 2px 12px rgba(15, 118, 110, 0.08); padding: 22px 26px 18px; }
  @media print { @page { size: auto; margin: 8mm; } body { padding: 0; background: #fff; } .card { box-shadow: none; border-radius: 0; padding: 0; } }
</style></head><body><div class="card">
${docType === 'sales' && paymentWatermark ? `<div style="position:absolute;left:50%;top:50%;transform:translate(-50%,-50%) rotate(-24deg);font-size:76px;font-weight:800;letter-spacing:8px;white-space:nowrap;color:${paymentWatermark === '已付款' ? '#047857' : '#b45309'};opacity:0.12;pointer-events:none;z-index:1">${escapeHtml(paymentWatermark)}</div>` : ''}
<div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:14px">
  <div>
    <div style="font-size:19px;font-weight:700;color:#0f766e;letter-spacing:1px">${escapeHtml(title)}</div>
    <div style="font-size:11px;color:#9ca3af;margin-top:3px">东莞市石龙昌发百货商店 · 13926846099</div>
  </div>
  <div style="text-align:right">
    <div style="display:inline-block;font-size:11px;font-weight:600;color:#0f766e;background:#e8f5f1;border-radius:6px;padding:3px 9px">${docType === 'inbound' ? '进货单' : docType === 'po' ? '采购单' : docType === 'quotation' ? '报价单' : docType === 'sales' ? '销售单' : '送货单'}</div>
    <div style="font-size:11px;color:#6b7280;margin-top:5px">单号 ${escapeHtml(quotationId)}</div>
  </div>
</div>
<div style="display:grid;grid-template-columns:1fr 1fr;gap:6px 16px;font-size:12px;color:#4b5563;margin-bottom:13px;padding-bottom:12px;border-bottom:1px dashed #e5e7eb">
  <div><span style="color:#9ca3af">${partyLabel}</span>&emsp;${escapeHtml(customerName || '—')}</div>
  <div><span style="color:#9ca3af">电话</span>&emsp;${escapeHtml(customerPhone || '—')}</div>
  <div><span style="color:#9ca3af">日期</span>&emsp;${escapeHtml(date)}</div>
  ${customerAddress ? `<div style="grid-column:1/3"><span style="color:#9ca3af">地址</span>&emsp;${escapeHtml(customerAddress)}</div>` : ''}
</div>
<table style="width:100%;border-collapse:collapse">
  <thead><tr style="background:#e8f5f1">${thead}</tr></thead>
  <tbody>${rows}</tbody>
</table>
${remark && (!template || template.showOrderRemark) ? `<div style="margin-top:10px;font-size:12px;color:#374151;line-height:1.5"><span style="color:#0f766e;font-weight:600">整单备注：</span>${escapeHtml(remark)}</div>` : ''}
${!template || template.showTotal ? `<div style="display:flex;justify-content:space-between;align-items:flex-end;margin-top:14px;padding:12px 2px 0;border-top:2px solid #0f766e">
  <div>
    <div style="font-size:12px;color:#6b7280;font-weight:600">${printHidePaid && displayPaidAmount > 0 ? '未付款合计' : '合计'}</div>
    ${displayPaidAmount > 0 ? `<div style="font-size:11px;color:#059669;margin-top:2px">已付款 ¥${displayPaidAmount.toFixed(2)}</div>` : ''}
  </div>
  <div style="font-size:25px;font-weight:700;color:#0f766e;font-variant-numeric:tabular-nums">¥ ${displayTotal.toFixed(2)}</div>
</div>` : ''}
${printHidePaid && displayPaidAmount > 0 ? '<div style="margin-top:8px;font-size:11px;color:#9ca3af">整单备注：总价不含已付款商品价格</div>' : ''}
${safeItems.some((it) => it.lineType === 'exchange' || it.lineType === 'return' || it.lineType === 'outOfStock') ? '<div style="margin-top:4px;font-size:11px;color:#9ca3af">退货扣款位置、补发及漏装情况见商品备注</div>' : ''}
${safeItems.some((it) => it.printPrice === false || it.showPrice === false || it.showAmount === false) ? '<div style="margin-top:4px;font-size:11px;color:#9ca3af">部分商品单价 / 金额已隐藏，总金额不含隐藏部分</div>' : ''}
${(!template || template.showFooter) ? '<div style="margin-top:16px;text-align:center;font-size:11px;color:#b0b7c3">感谢惠顾 · 欢迎再次光临</div>' : ''}
</div></body></html>`;
}

function escapeHtml(text: string): string {
  if (text == null) return '';
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
